import Link from 'next/link';
import { useMemo } from 'react';
import { convertStringToSlug, getCurrentYear } from 'ui/helpers/utils';
import eventClickPromo from 'ui/lib/gtm/eventClickPromo';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import { useRouter } from 'next/router';
import Head from 'next/head';

interface IPromoBannerSectionProps {
  promoHero: {
    id: number;
    slug: string;
    titlePage: string;
    promoTitle?: string;
    promoDescription?: string;
    contentCategory: {
      name: string;
    };
    heroImageLink: string;
    altImage: string;
    heroLinkResizedImage: string;
  };
  city: string;
  handleCollectDataLayer: (data: {
    id: number;
    name: string;
    creative: string;
    position: string;
    category: string;
  }) => void;
}

const PromoBannerSection = ({
  promoHero,
  city = '',
  handleCollectDataLayer,
}: IPromoBannerSectionProps) => {
  const currentYear = getCurrentYear();
  const PUBLIC_URL = process.env.NEXT_PUBLIC_BASEPATH_URL;
  // Path for detail promo is --> /promosi/[category]/[slug]
  const detailLink = useMemo(() => {
    const formattedCategoryName = convertStringToSlug(promoHero.contentCategory.name);
    return `${PUBLIC_URL}/${formattedCategoryName}/${promoHero.slug}`;
  }, [promoHero]);
  const router = useRouter();

  const bannerRef = useIsElementVisible(
    () =>
      handleCollectDataLayer({
        id: promoHero?.id,
        name: (promoHero?.promoTitle ? promoHero?.promoTitle : promoHero?.titlePage)?.toLowerCase(),
        creative: `/promosi/${promoHero?.contentCategory?.name
          ?.toLowerCase()
          ?.replace(/ /g, '-')}/${promoHero?.slug}`,
        position: `1 - promosi ${promoHero?.contentCategory?.name
          ?.toLowerCase()
          ?.replace(/-/g, ' ')}`,
        category: (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
          ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
          : 'semua',
      }),
    [promoHero]
  );

  return (
    <section className="mt-[10px] mb-[16px] lg:mt-[20px] lg:mb-[32px]">
      <Head>
        <link rel="preload" as="image" href={promoHero?.heroImageLink} imageSizes="50vw" />
      </Head>
      <div className="container xl:px-0">
        <h1 className="text-base font-bold uppercase text-reliableBlack lg:text-2xl lg:mb-2">
          {promoHero.promoTitle || `Promo Toyota Terbaru ${currentYear} ${city}`}
        </h1>
        <p className="text-xs font-medium leading-relaxed text-reliableBlack mb-5 lg:text-base lg:font-normal lg:mb-9">
          {promoHero.promoDescription ||
            'Penawaran dan promo mobil Toyota online terbaru dari Auto2000 untuk Anda'}
        </p>
      </div>

      {(promoHero?.heroImageLink || promoHero?.heroLinkResizedImage) && (
        <div className="lg:container xl:px-0">
          <Link
            ref={bannerRef}
            id="hero-banner-promo"
            href={detailLink ?? '/'}
            onClick={() =>
              eventClickPromo({
                title: (promoHero?.promoTitle
                  ? promoHero?.promoTitle
                  : promoHero?.titlePage
                )?.toLowerCase(),
                slug: promoHero?.slug,
                category: promoHero?.contentCategory?.name,
                id: promoHero?.id,
                index: 1,
                anotherCategory: (router?.query?.kategori as string)
                  ?.toLowerCase()
                  ?.replace(/-/g, ' ')
                  ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
                  : 'semua',
              })
            }
          >
            <img
              src={promoHero?.heroImageLink || promoHero?.heroLinkResizedImage}
              alt={promoHero?.altImage}
              fetchPriority="high"
              loading="eager"
              width={1024}
              height={450}
              className="w-full object-cover aspect-video object-center md:hidden"
            />
            <img
              src={promoHero?.heroLinkResizedImage || promoHero?.heroImageLink}
              alt={promoHero?.altImage}
              fetchPriority="high"
              loading="eager"
              width={1024}
              height={450}
              className="w-full object-cover aspect-video object-center h-[450px] hidden md:block"
            />
          </Link>
        </div>
      )}
    </section>
  );
};

export default PromoBannerSection;
